import { useState, useEffect, useContext } from "react";
import { AlertModal } from "@/components/AlertModal";
import { Save, Globe, Share2, Lock, User, DollarSign, Upload, Play, Trash2, Mail, Key, CreditCard, Settings } from "lucide-react";
import axios from "axios";
import { API, SettingsContext } from "@/App";

export default function AdminSettings() {
  const { settings, setSettings } = useContext(SettingsContext);
  const [localSettings, setLocalSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingFavicon, setIsUploadingFavicon] = useState(false);
  const [isUploadingPlayerLogo, setIsUploadingPlayerLogo] = useState(false);
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: "", message: "" });

  useEffect(() => {
    axios.get(`${API}/settings/admin`, { withCredentials: true }).then((res) => {
      setLocalSettings(res.data);
      setLoading(false);
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!localSettings) return;
    setSaving(true);
    await axios.post(`${API}/settings`, localSettings, { withCredentials: true });
    setSettings(localSettings);
    setSaving(false);
    setAlertModal({
      isOpen: true,
      title: "Success",
      message: "Settings saved successfully!"
    });
  };

  const handleUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (type === 'logo') setIsUploadingLogo(true);
    else if (type === 'favicon') setIsUploadingFavicon(true);
    else setIsUploadingPlayerLogo(true);

    const formDataUpload = new FormData();
    formDataUpload.append("file", file);

    try {
      const res = await axios.post(`${API}/upload`, formDataUpload, { withCredentials: true });
      if (res.data.url) {
        if (type === 'logo') {
          setLocalSettings(prev => ({ ...prev, logo_url: res.data.url }));
        } else if (type === 'favicon') {
          setLocalSettings(prev => ({ ...prev, favicon_url: res.data.url }));
        } else {
          setLocalSettings(prev => ({ ...prev, player_logo_url: res.data.url }));
        }
      }
    } catch (error) {
      setAlertModal({ isOpen: true, title: "Upload Failed", message: error.response?.data?.detail || "File upload failed. Please try again." });
    } finally {
      if (type === 'logo') setIsUploadingLogo(false);
      else if (type === 'favicon') setIsUploadingFavicon(false);
      else setIsUploadingPlayerLogo(false);
    }
  };

  if (loading) return null;

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-8" data-testid="settings-form">
        {/* General Settings */}
        <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
          <div className="p-6 border-b border-slate-800 flex items-center gap-2">
            <Globe size={20} className="text-slate-500" />
            <h3 className="font-bold text-white">General Information</h3>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-400">Site Name</label>
              <input
                type="text"
                data-testid="site-name-input"
                className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                value={localSettings?.site_name || ""}
                onChange={(e) => setLocalSettings({ ...localSettings, site_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-400">Contact Email</label>
              <input
                type="email"
                data-testid="contact-email-input"
                className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                value={localSettings?.contact_email || ""}
                onChange={(e) => setLocalSettings({ ...localSettings, contact_email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-400">Admin Email (for password reset)</label>
              <input
                type="email"
                data-testid="admin-email-input"
                className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                value={localSettings?.admin_email || ""}
                onChange={(e) => setLocalSettings({ ...localSettings, admin_email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-400">Logo</label>
              <div className="flex items-center gap-4">
                {localSettings?.logo_url && (
                  <div className="w-12 h-12 rounded-xl bg-slate-950 border border-slate-800 p-1 flex-shrink-0">
                    <img src={localSettings.logo_url} alt="Logo preview" className="w-full h-full object-contain" />
                  </div>
                )}
                <div className="flex-grow relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleUpload(e, 'logo')}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={isUploadingLogo}
                  />
                  <div className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-slate-400 flex items-center justify-center gap-2 hover:bg-slate-900 transition-colors">
                    {isUploadingLogo ? (
                      <span className="animate-pulse">Uploading...</span>
                    ) : (
                      <>
                        <Upload size={18} />
                        <span>{localSettings?.logo_url ? "Change Logo" : "Upload Logo"}</span>
                      </>
                    )}
                  </div>
                </div>
                {localSettings?.logo_url && (
                  <button
                    type="button"
                    onClick={() => setLocalSettings(prev => ({ ...prev, logo_url: "" }))}
                    data-testid="delete-logo-btn"
                    className="p-2 rounded-xl border border-red-500/20 bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white transition-colors flex-shrink-0"
                    title="Delete Logo"
                  >
                    <Trash2 size={18} />
                  </button>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-400">Favicon</label>
              <div className="flex items-center gap-4">
                {localSettings?.favicon_url && (
                  <div className="w-12 h-12 rounded-xl bg-slate-950 border border-slate-800 p-1 flex-shrink-0">
                    <img src={localSettings.favicon_url} alt="Favicon preview" className="w-full h-full object-contain" />
                  </div>
                )}
                <div className="flex-grow relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleUpload(e, 'favicon')}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={isUploadingFavicon}
                  />
                  <div className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-slate-400 flex items-center justify-center gap-2 hover:bg-slate-900 transition-colors">
                    {isUploadingFavicon ? (
                      <span className="animate-pulse">Uploading...</span>
                    ) : (
                      <>
                        <Upload size={18} />
                        <span>{localSettings?.favicon_url ? "Change Favicon" : "Upload Favicon"}</span>
                      </>
                    )}
                  </div>
                </div>
                {localSettings?.favicon_url && (
                  <button
                    type="button"
                    onClick={() => setLocalSettings(prev => ({ ...prev, favicon_url: "" }))}
                    data-testid="delete-favicon-btn"
                    className="p-2 rounded-xl border border-red-500/20 bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white transition-colors flex-shrink-0"
                    title="Delete Favicon"
                  >
                    <Trash2 size={18} />
                  </button>
                )}
              </div>
            </div>
            <div className="md:col-span-2 space-y-2">
              <label className="text-sm font-bold text-slate-400">Footer Description</label>
              <textarea
                rows={3}
                className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                value={localSettings?.footer_description || ""}
                onChange={(e) => setLocalSettings({ ...localSettings, footer_description: e.target.value })}
              />
            </div>
            <div className="md:col-span-2 space-y-2">
              <label className="text-sm font-bold text-slate-400">Footer Text (Copyright)</label>
              <textarea
                rows={2}
                className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                value={localSettings?.footer_text || ""}
                onChange={(e) => setLocalSettings({ ...localSettings, footer_text: e.target.value })}
              />
            </div>
          </div>
        </div>

        {/* Player Settings */}
        <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
          <div className="p-6 border-b border-slate-800 flex items-center gap-2">
            <Play size={20} className="text-slate-500" />
            <h3 className="font-bold text-white">Player Settings</h3>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-400">Player Logo</label>
              <div className="flex items-center gap-4">
                {localSettings?.player_logo_url && (
                  <div className="w-12 h-12 rounded-xl bg-slate-950 border border-slate-800 p-1 flex-shrink-0">
                    <img src={localSettings.player_logo_url} alt="Player Logo preview" className="w-full h-full object-contain" />
                  </div>
                )}
                <div className="flex-grow relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleUpload(e, 'player_logo')}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={isUploadingPlayerLogo}
                  />
                  <div className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-slate-400 flex items-center justify-center gap-2 hover:bg-slate-900 transition-colors">
                    {isUploadingPlayerLogo ? (
                      <span className="animate-pulse">Uploading...</span>
                    ) : (
                      <>
                        <Upload size={18} />
                        <span>{localSettings?.player_logo_url ? "Change Player Logo" : "Upload Player Logo"}</span>
                      </>
                    )}
                  </div>
                </div>
                {localSettings?.player_logo_url && (
                  <button
                    type="button"
                    onClick={() => setLocalSettings(prev => ({ ...prev, player_logo_url: "" }))}
                    data-testid="delete-player-logo-btn"
                    className="p-2 rounded-xl border border-red-500/20 bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white transition-colors flex-shrink-0"
                    title="Delete Player Logo"
                  >
                    <Trash2 size={18} />
                  </button>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-400">Player Logo Position</label>
              <select
                className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                value={localSettings?.player_logo_position || 'top-right'}
                onChange={(e) => setLocalSettings({ ...localSettings, player_logo_position: e.target.value })}
              >
                <option value="top-left">Top Left</option>
                <option value="top-right">Top Right</option>
                <option value="bottom-left">Bottom Left</option>
                <option value="bottom-right">Bottom Right</option>
              </select>
            </div>
          </div>
        </div>

        {/* Site Controls */}
        <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
          <div className="p-6 border-b border-slate-800 flex items-center gap-2">
            <Settings size={20} className="text-slate-500" />
            <h3 className="font-bold text-white">Site Controls</h3>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-400">Default Language</label>
              <select
                className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                value={localSettings?.default_language || "fr"}
                onChange={(e) => setLocalSettings({ ...localSettings, default_language: e.target.value })}
                data-testid="default-language-select"
              >
                <option value="fr">Français</option>
                <option value="en">English</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-400">Timezone</label>
              <input
                type="text"
                className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                value={localSettings?.timezone || "Africa/Kinshasa"}
                onChange={(e) => setLocalSettings({ ...localSettings, timezone: e.target.value })}
                placeholder="Africa/Kinshasa"
              />
            </div>
            <div className="flex items-center justify-between md:col-span-2 bg-slate-950 p-4 rounded-xl border border-slate-800">
              <div>
                <h4 className="text-sm font-bold text-white">Maintenance Mode</h4>
                <p className="text-xs text-slate-500 mt-0.5">When enabled, the site shows a maintenance page to non-admin visitors.</p>
              </div>
              <button
                type="button"
                data-testid="maintenance-toggle"
                onClick={() => setLocalSettings(prev => ({ ...prev, maintenance_mode: !prev?.maintenance_mode }))}
                className={`relative w-12 h-6 rounded-full transition-colors ${localSettings?.maintenance_mode ? 'bg-red-600' : 'bg-slate-700'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${localSettings?.maintenance_mode ? 'translate-x-6' : ''}`} />
              </button>
            </div>
            <div className="flex items-center justify-between md:col-span-2 bg-slate-950 p-4 rounded-xl border border-slate-800">
              <div>
                <h4 className="text-sm font-bold text-white">User Registration</h4>
                <p className="text-xs text-slate-500 mt-0.5">Allow new users to register on the platform.</p>
              </div>
              <button
                type="button"
                data-testid="registration-toggle"
                onClick={() => setLocalSettings(prev => ({ ...prev, registration_enabled: prev?.registration_enabled === false ? true : !(prev?.registration_enabled) }))}
                className={`relative w-12 h-6 rounded-full transition-colors ${localSettings?.registration_enabled !== false ? 'bg-blue-600' : 'bg-slate-700'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${localSettings?.registration_enabled !== false ? 'translate-x-6' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* SEO Defaults */}
        <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
          <div className="p-6 border-b border-slate-800 flex items-center gap-2">
            <Globe size={20} className="text-slate-500" />
            <h3 className="font-bold text-white">SEO Defaults</h3>
          </div>
          <div className="p-6 grid grid-cols-1 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-400">Meta Title</label>
              <input
                type="text"
                className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                value={localSettings?.seo_title || ""}
                onChange={(e) => setLocalSettings({ ...localSettings, seo_title: e.target.value })}
                placeholder="DJILU.COM - African IPTV Streaming"
                data-testid="seo-title-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-400">Meta Description</label>
              <textarea
                rows={2}
                className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                value={localSettings?.seo_description || ""}
                onChange={(e) => setLocalSettings({ ...localSettings, seo_description: e.target.value })}
                placeholder="Watch live TV channels from across Africa..."
                data-testid="seo-description-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-400">Analytics ID (Google Analytics)</label>
              <input
                type="text"
                className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                value={localSettings?.analytics_id || ""}
                onChange={(e) => setLocalSettings({ ...localSettings, analytics_id: e.target.value })}
                placeholder="G-XXXXXXXXXX"
              />
            </div>
          </div>
        </div>

        {/* Membership Toggle */}
        <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
          <div className="p-6 border-b border-slate-800 flex items-center gap-2">
            <CreditCard size={20} className="text-slate-500" />
            <h3 className="font-bold text-white">Membership System</h3>
          </div>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-bold text-white">Enable Membership</h4>
                <p className="text-xs text-slate-500 mt-0.5">When disabled, all membership plans, premium badges, and subscription options are hidden from the website.</p>
              </div>
              <button
                type="button"
                data-testid="membership-toggle"
                onClick={() => setLocalSettings(prev => ({ ...prev, membership_enabled: !prev?.membership_enabled }))}
                className={`relative w-12 h-6 rounded-full transition-colors ${localSettings?.membership_enabled !== false ? 'bg-blue-600' : 'bg-slate-700'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${localSettings?.membership_enabled !== false ? 'translate-x-6' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Admin Credentials */}
        <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
          <div className="p-6 border-b border-slate-800 flex items-center gap-2">
            <Lock size={20} className="text-slate-500" />
            <h3 className="font-bold text-white">Admin Credentials</h3>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-400 flex items-center gap-2">
                <User size={16} /> Admin Username
              </label>
              <input
                type="text"
                data-testid="admin-username-input"
                className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                value={localSettings?.admin_username || ""}
                onChange={(e) => setLocalSettings({ ...localSettings, admin_username: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-400 flex items-center gap-2">
                <Lock size={16} /> Admin Password
              </label>
              <input
                type="text"
                data-testid="admin-password-input"
                className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                value={localSettings?.admin_password || ""}
                onChange={(e) => setLocalSettings({ ...localSettings, admin_password: e.target.value })}
              />
            </div>
          </div>
        </div>

        {/* Donation Links */}
        <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
          <div className="p-6 border-b border-slate-800 flex items-center gap-2">
            <DollarSign size={20} className="text-slate-500" />
            <h3 className="font-bold text-white">Donation Links</h3>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-400">CashApp Link</label>
              <input
                type="url"
                placeholder="https://cash.app/$yourname"
                className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                value={localSettings?.donation_cashapp || ""}
                onChange={(e) => setLocalSettings({ ...localSettings, donation_cashapp: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-400">PayPal Link</label>
              <input
                type="url"
                placeholder="https://paypal.me/yourname"
                className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                value={localSettings?.donation_paypal || ""}
                onChange={(e) => setLocalSettings({ ...localSettings, donation_paypal: e.target.value })}
              />
            </div>
          </div>
        </div>

        {/* Email Configuration */}
        <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
          <div className="p-6 border-b border-slate-800 flex items-center gap-2">
            <Mail size={20} className="text-slate-500" />
            <h3 className="font-bold text-white">Email Configuration</h3>
          </div>
          <div className="p-6 space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-400 flex items-center gap-2">
                <Key size={14} /> Resend API Key
              </label>
              <input
                type="password"
                data-testid="resend-api-key-input"
                placeholder="re_xxxxxxxxx..."
                className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 font-mono"
                value={localSettings?.resend_api_key || ""}
                onChange={(e) => setLocalSettings({ ...localSettings, resend_api_key: e.target.value })}
              />
              <p className="text-xs text-slate-600">Get your API key from <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">resend.com</a>. Used for password reset emails.</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-400">Sender Email</label>
              <input
                type="email"
                data-testid="sender-email-input"
                placeholder="onboarding@resend.dev"
                className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                value={localSettings?.sender_email || ""}
                onChange={(e) => setLocalSettings({ ...localSettings, sender_email: e.target.value })}
              />
              <p className="text-xs text-slate-600">The "from" address for outgoing emails. Must be verified in Resend or use the default.</p>
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
          <div className="p-6 border-b border-slate-800 flex items-center gap-2">
            <Share2 size={20} className="text-slate-500" />
            <h3 className="font-bold text-white">Social Media Links</h3>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-400">X (Twitter)</label>
              <input
                type="url"
                className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                value={localSettings?.social_x || ""}
                onChange={(e) => setLocalSettings({ ...localSettings, social_x: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-400">Facebook</label>
              <input
                type="url"
                className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                value={localSettings?.social_facebook || ""}
                onChange={(e) => setLocalSettings({ ...localSettings, social_facebook: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-400">Instagram</label>
              <input
                type="url"
                className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                value={localSettings?.social_instagram || ""}
                onChange={(e) => setLocalSettings({ ...localSettings, social_instagram: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-400">YouTube</label>
              <input
                type="url"
                className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                value={localSettings?.social_youtube || ""}
                onChange={(e) => setLocalSettings({ ...localSettings, social_youtube: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-400">TikTok</label>
              <input
                type="url"
                className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                value={localSettings?.social_tiktok || ""}
                onChange={(e) => setLocalSettings({ ...localSettings, social_tiktok: e.target.value })}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            data-testid="save-settings-btn"
            className="flex items-center gap-2 px-8 py-4 bg-blue-600 text-white rounded-2xl font-bold hover:bg-blue-500 transition-all shadow-lg shadow-blue-600/20 disabled:opacity-50"
          >
            <Save size={20} />
            {saving ? "Saving..." : "Save All Settings"}
          </button>
        </div>
      </form>

      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        onClose={() => setAlertModal(prev => ({ ...prev, isOpen: false }))}
      />
    </>
  );
}
